/**
 * FUEL IQ DESIGN SYSTEM 2.0 - MATERIAL-UI THEME
 *
 * World-class theme configuration using our design tokens.
 * Provides consistent, beautiful, and accessible UI components.
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';
import { designTokens } from './designTokens';

const { colors, typography, shadows, gradients, transitions, borderRadius, spacing } = designTokens;

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'dark',
    primary: {
      main: colors.primary[500],
      light: colors.primary[400],
      dark: colors.primary[700],
      contrastText: colors.neutral[900],
    },
    secondary: {
      main: colors.secondary.magenta[500],
      light: colors.secondary.magenta[400],
      dark: colors.secondary.magenta[700],
      contrastText: colors.neutral[50],
    },
    info: {
      main: colors.secondary.blue[500],
      light: colors.secondary.blue[400],
      dark: colors.secondary.blue[700],
      contrastText: colors.neutral[900],
    },
    success: {
      main: colors.semantic.success,
      light: colors.primary[400],
      dark: colors.primary[700],
      contrastText: colors.neutral[900],
    },
    warning: {
      main: colors.semantic.warning,
      light: '#ffff4d',
      dark: '#ccc700',
      contrastText: colors.neutral[900],
    },
    error: {
      main: colors.semantic.error,
      light: '#ff4d73',
      dark: '#cc0033',
      contrastText: colors.neutral[50],
    },
    background: {
      default: colors.background.primary,
      paper: colors.background.secondary,
    },
    text: {
      primary: colors.neutral[50],
      secondary: colors.neutral[300],
      disabled: colors.neutral[500],
    },
    divider: 'rgba(255, 255, 255, 0.12)',
  },

  typography: {
    fontFamily: typography.fontFamily.sans,
    fontSize: 16,
    fontWeightLight: typography.fontWeight.light,
    fontWeightRegular: typography.fontWeight.normal,
    fontWeightMedium: typography.fontWeight.medium,
    fontWeightBold: typography.fontWeight.bold,

    h1: {
      fontFamily: typography.fontFamily.display,
      fontSize: typography.fontSize['5xl'],
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.lineHeight.tight,
      letterSpacing: typography.letterSpacing.tight,
    },
    h2: {
      fontFamily: typography.fontFamily.display,
      fontSize: typography.fontSize['4xl'],
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.lineHeight.tight,
      letterSpacing: typography.letterSpacing.tight,
    },
    h3: {
      fontFamily: typography.fontFamily.display,
      fontSize: typography.fontSize['3xl'],
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.snug,
    },
    h4: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.snug,
    },
    h5: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.snug,
    },
    h6: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.normal,
    },
    subtitle1: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.normal,
    },
    subtitle2: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.normal,
    },
    body1: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.normal,
      lineHeight: typography.lineHeight.relaxed,
    },
    body2: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.normal,
      lineHeight: typography.lineHeight.normal,
    },
    button: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.semibold,
      textTransform: 'none',
      letterSpacing: typography.letterSpacing.wide,
    },
    caption: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.normal,
      lineHeight: typography.lineHeight.normal,
    },
    overline: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.semibold,
      textTransform: 'uppercase',
      letterSpacing: typography.letterSpacing.wider,
      lineHeight: typography.lineHeight.normal,
    },
  },

  shape: {
    borderRadius: 8, // 8px — consistent squared-off cyberpunk look
  },

  shadows: [
    'none',
    shadows.dark.sm,
    shadows.dark.sm,
    shadows.dark.md,
    shadows.dark.md,
    shadows.dark.md,
    shadows.dark.lg,
    shadows.dark.lg,
    shadows.dark.lg,
    shadows.dark.xl,
    shadows.dark.xl,
    shadows.dark.xl,
    shadows.dark.floating,
    shadows.dark.floating,
    shadows.dark.elevated,
    shadows.dark.elevated,
    shadows.dark.elevated,
    shadows.dark.elevated,
    shadows.dark.elevated,
    shadows.dark.elevated,
    shadows.dark.elevated,
    shadows.dark.elevated,
    shadows.dark.elevated,
    shadows.dark.elevated,
    shadows.dark.elevated,
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: `${colors.neutral[700]} ${colors.background.primary}`,
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: colors.background.primary,
          },
          '&::-webkit-scrollbar-thumb': {
            background: colors.neutral[700],
            borderRadius: borderRadius.base,
            '&:hover': {
              background: colors.neutral[600],
            },
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          background: 'var(--bg-card)', // Controlled by global.css CSS variable
          border: '1px solid rgba(0,224,255,0.1)',
          borderRadius: '8px',
          boxShadow: 'none',
          transition: `all ${transitions.duration.base} ${transitions.timing.smooth}`,
          position: 'relative',
          overflow: 'hidden',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'var(--bg-card)',
          border: '1px solid rgba(0,224,255,0.1)',
          borderRadius: '8px',
        },
        elevation1: {
          boxShadow: shadows.dark.sm,
        },
        elevation2: {
          boxShadow: shadows.dark.md,
        },
        elevation3: {
          boxShadow: shadows.dark.lg,
        },
        elevation4: {
          boxShadow: shadows.dark.xl,
        },
      },
    },

    // Instrument panel (2026-11-03): every MUI button matches PanelButton — filled cyan
    // for the one main action, cyan outline for the rest, quiet text otherwise.
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '8px',
          padding: '8px 16px',
          fontSize: '0.8rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          border: 'none',
          transition: `all ${transitions.duration.base} ${transitions.timing.smooth}`,
          position: 'relative',
          overflow: 'hidden',
          '&:focus': { outline: 'none', boxShadow: 'none' },
          '&:focus-visible': { outline: '2px solid #00E0FF', outlineOffset: '2px' },
        },
        contained: {
          background: '#00E0FF !important',
          backgroundColor: '#00E0FF !important',
          color: '#0a0e14 !important',
          fontWeight: 700,
          boxShadow: '0 0 14px rgba(0,224,255,0.28)',
          '&:hover': { background: '#5CEBFF !important', backgroundColor: '#5CEBFF !important', boxShadow: '0 0 18px rgba(0,224,255,0.4)' },
          '&.Mui-disabled': {
            background: 'rgba(255, 255, 255, 0.06) !important',
            backgroundColor: 'transparent !important',
            color: 'rgba(255, 255, 255, 0.3) !important',
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          background: '#00E0FF !important',
          backgroundColor: '#00E0FF !important',
          color: '#0a0e14 !important',
        },
        containedSecondary: {
          background: 'transparent !important',
          backgroundColor: 'transparent !important',
          color: '#00E0FF !important',
          border: '1px solid rgba(0,224,255,0.5)',
          boxShadow: 'none',
          '&:hover': { background: 'rgba(0,224,255,0.08) !important', backgroundColor: 'rgba(0,224,255,0.08) !important' },
        },
        outlined: {
          border: '1px solid rgba(0,224,255,0.5) !important',
          background: 'transparent',
          color: '#00E0FF',
          '&:hover': { border: '1px solid #00E0FF !important', background: 'rgba(0,224,255,0.08)' },
        },
        outlinedPrimary: {
          borderColor: 'rgba(0,224,255,0.5) !important',
          color: '#00E0FF !important',
          '&:hover': { borderColor: '#00E0FF !important', background: 'rgba(0,224,255,0.08)' },
        },
        text: {
          color: 'rgba(255,255,255,0.7)',
          '&:hover': { background: 'rgba(0,224,255,0.06)', color: '#fff' },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
          transition: `all ${transitions.duration.fast} ${transitions.timing.smooth}`,
          '&:hover': {
            background: 'rgba(0, 224, 255, 0.08)',
          },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(10px)',
            borderRadius: borderRadius.md,
            transition: `all ${transitions.duration.base} ${transitions.timing.smooth}`,
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.12)',
              borderWidth: '1.5px',
              transition: `all ${transitions.duration.base} ${transitions.timing.smooth}`,
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused': {
              background: 'rgba(255, 255, 255, 0.05)',
              boxShadow: `0 0 0 3px rgba(0, 224, 255, 0.1)`,
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00E0FF',
              borderWidth: '2px',
            },
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: 700,
          fontSize: '0.7rem',
          letterSpacing: 0.5,
          height: '26px',
          border: 'none',
          transition: `all ${transitions.duration.base} ${transitions.timing.smooth}`,
        },
        filled: {
          background: 'rgba(255, 255, 255, 0.12)',
          backdropFilter: 'blur(10px)',
          color: colors.neutral[100],
        },
        colorPrimary: {
          background: 'rgba(0, 224, 255, 0.14)',
          color: '#00E0FF',
          backdropFilter: 'blur(10px)',
        },
        colorSecondary: {
          background: 'rgba(255, 0, 230, 0.18)',
          color: colors.secondary.magenta[400],
          backdropFilter: 'blur(10px)',
        },
        colorSuccess: {
          background: 'rgba(0, 230, 118, 0.14)',
          color: '#00E676',
        },
        colorError: {
          background: 'rgba(255, 23, 68, 0.14)',
          color: colors.semantic.error,
        },
        colorWarning: {
          background: 'rgba(255, 215, 64, 0.14)',
          color: '#FFD740',
        },
        colorInfo: {
          background: 'rgba(0, 224, 255, 0.14)',
          color: '#00E0FF',
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 6,
          borderRadius: '2px',
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          overflow: 'hidden',
        },
        bar: {
          borderRadius: '2px',
          background: '#00E0FF',
          boxShadow: '0 0 6px rgba(0,224,255,0.5)',
        },
      },
    },

    MuiDialog: {
      // disableScrollLock prevents MUI from setting
      // `document.body.style.overflow = 'hidden'` when a dialog opens. If a
      // dialog fails to unmount cleanly (e.g. during a notification deep-link
      // nav), the scroll lock persists and iOS Safari freezes the page scroll.
      // Applied globally to all dialogs — scroll-behind is acceptable UX for
      // this app since all dialogs use the cyberpunk card background.
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        paper: {
          background: 'var(--bg-card)',
          backdropFilter: 'blur(40px) saturate(200%)',
          border: '1px solid rgba(0,224,255,0.3)',
          borderRadius: '12px',
          boxShadow: `${shadows.dark.xl}, 0 0 24px rgba(0,224,255,0.08)`,
        },
      },
    },

    MuiDrawer: {
      defaultProps: {
        ModalProps: {
          disableScrollLock: true,
        },
      },
    },

    MuiSwipeableDrawer: {
      defaultProps: {
        ModalProps: {
          disableScrollLock: true,
        },
      },
    },

    MuiModal: {
      defaultProps: {
        disableScrollLock: true,
      },
    },

    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
    },

    MuiBackdrop: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(12px)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          background: gradients.background.card,
          backdropFilter: 'blur(20px) saturate(180%)',
          border: `1px solid rgba(255, 255, 255, 0.12)`,
          borderRadius: borderRadius.lg,
          boxShadow: shadows.dark.elevated,
          marginTop: '8px',
        },
        list: {
          padding: '8px',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.base,
          margin: '2px 0',
          padding: '10px 16px',
          transition: `all ${transitions.duration.fast} ${transitions.timing.smooth}`,
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.08)',
            transform: 'translateX(4px)',
          },
          '&.Mui-selected': {
            background: 'rgba(0, 255, 107, 0.12)',
            '&:hover': {
              background: 'rgba(0, 255, 107, 0.16)',
            },
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: gradients.background.elevated,
          backdropFilter: 'blur(16px)',
          border: `1px solid rgba(255, 255, 255, 0.12)`,
          borderRadius: borderRadius.base,
          padding: '8px 12px',
          fontSize: typography.fontSize.xs,
          boxShadow: shadows.dark.lg,
        },
        arrow: {
          color: colors.background.elevated,
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          backdropFilter: 'blur(10px)',
          border: '1.5px solid',
        },
        filledSuccess: {
          background: 'rgba(0, 255, 107, 0.15)',
          borderColor: colors.semantic.success,
          color: colors.neutral[50],
        },
        filledError: {
          background: 'rgba(255, 0, 64, 0.15)',
          borderColor: colors.semantic.error,
          color: colors.neutral[50],
        },
        filledWarning: {
          background: 'rgba(250, 255, 0, 0.15)',
          borderColor: colors.semantic.warning,
          color: colors.neutral[900],
        },
        filledInfo: {
          background: 'rgba(0, 224, 255, 0.15)',
          borderColor: colors.semantic.info,
          color: colors.neutral[50],
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        indicator: {
          height: 2,
          borderRadius: borderRadius.full,
          background: 'rgba(255, 255, 255, 0.4)',
          boxShadow: 'none',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: typography.fontWeight.medium,
          fontSize: typography.fontSize.sm,
          minHeight: 44,
          borderRadius: borderRadius.md,
          color: colors.neutral[400],
          transition: `all ${transitions.duration.fast} ${transitions.timing.smooth}`,
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.05)',
            color: colors.neutral[200],
          },
          '&.Mui-selected': {
            color: colors.neutral[50],
            fontWeight: typography.fontWeight.semibold,
          },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 48,
          height: 28,
          padding: 0,
        },
        switchBase: {
          padding: 4,
          '&.Mui-checked': {
            transform: 'translateX(20px)',
            color: colors.neutral[50],
            '& + .MuiSwitch-track': {
              backgroundColor: 'rgba(0, 224, 255, 0.85)',
              opacity: 1,
              border: 'none',
            },
          },
        },
        thumb: {
          width: 20,
          height: 20,
          backgroundColor: colors.neutral[50],
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
        },
        track: {
          borderRadius: borderRadius.full,
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
          opacity: 1,
          border: 'none',
        },
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
          gap: spacing[3],
        },
        label: {
          fontSize: typography.fontSize.sm,
          color: colors.neutral[200],
        },
      },
    },
  },
};

export const theme = createTheme(themeOptions);
export default theme;
